import React from 'react'
import SEO from '../components/SEO'
import ContactForm from '../components/ContactForm'
import CMSGallery from '../components/CMSGallery'

export default function Contact() {
  return (
    <div className="space-y-0">
      <SEO
        title="Kontakt Transport4U | Bestil Transport 24/7"
        description="Kontakt Transport4U for hurtig bestilling af godstransport og temperaturstyret logistik. Bestillingsformular, e-mail transport4u.dk@gmail.com, tlf. 50 35 20 35, Herningvej 108, 8600 Silkeborg."
      />

      {/* Hero */}
      <section className="bg-navy-950 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Døgnåben kundebetjening</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
              Kontakt Transport4U
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Send din transportforespørgsel via bestillingsformularen herunder, skriv direkte til os på <a href="mailto:transport4u.dk@gmail.com" className="text-sky-400 font-bold hover:underline">transport4u.dk@gmail.com</a>, eller ring på <a href="tel:+4550352035" className="text-sky-400 font-bold hover:underline whitespace-nowrap">50 35 20 35</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Grid Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left: Contact Info & Address */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white rounded-2xl p-8 border border-slate-200/90 shadow-xs space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
                    Direkte kontakt
                  </span>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                    Vi er altid til at få fat på
                  </h2>
                </div>

                <div className="space-y-5 text-sm">
                  {/* Phone */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
                        Telefon (Døgnåben 24/7)
                      </span>
                      <a
                        href="tel:+4550352035"
                        className="text-xl font-extrabold text-navy-900 hover:text-sky-600 transition-colors block mt-0.5 whitespace-nowrap"
                        aria-label="Ring til 50 35 20 35"
                      >
                        <span className="whitespace-nowrap">50 35 20 35</span>
                      </a>
                      <span className="text-xs text-slate-600 mt-1 block">
                        Ring ved akutte udrykninger og spørgsmål
                      </span>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
                        E-mail
                      </span>
                      <a
                        href="mailto:transport4u.dk@gmail.com"
                        className="text-base font-bold text-navy-900 hover:text-sky-600 transition-colors block mt-0.5 break-all"
                        aria-label="Send e-mail til transport4u.dk@gmail.com"
                      >
                        transport4u.dk@gmail.com
                      </a>
                      <span className="text-xs text-slate-600 mt-1 block">
                        Skriv direkte til os for spørgsmål og tilbud
                      </span>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-white">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
                        Adresse & CVR
                      </span>
                      <div className="text-sm font-bold text-slate-900 mt-0.5">
                        Transport4U
                      </div>
                      <div className="text-slate-600 text-sm">
                        Herningvej 108
                      </div>
                      <div className="text-slate-600 text-sm">
                        8600 Silkeborg
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        CVR 44899345
                      </div>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-white">
                      <i className="fa-solid fa-clock" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
                        Åbningstider & Beredskab
                      </span>
                      <span className="text-sm font-bold text-slate-900 mt-0.5 block">
                        24 timer i døgnet / 7 dage om ugen
                      </span>
                      <span className="text-xs text-slate-600 mt-0.5 block">
                        Vi kører også i weekender og på alle helligdage
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick facts bullet */}
                <div className="pt-4 border-t border-slate-100 text-xs text-slate-600 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-shield-halved text-sky-600" aria-hidden="true" />
                    <span>Fuld godsforsikring på samtlige transporter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-bolt text-sky-600" aria-hidden="true" />
                    <span>Mulighed for afgang på ruten inden for 2 timer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-pallet text-sky-600" aria-hidden="true" />
                    <span>Kapacitet fra 1 kg op til 8 europaller</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: The Webform */}
            <div className="lg:col-span-7">
              <div className="space-y-4">
                <div className="bg-sky-50 rounded-2xl p-6 border border-sky-200/60 mb-6">
                  <h3 className="text-sm font-bold text-sky-950 flex items-center gap-2">
                    <i className="fa-solid fa-paper-plane text-sky-600" aria-hidden="true" />
                    <span>Send en forespørgsel</span>
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    Udfyld formularen herunder med information om dit gods, ønsket dato og rute. Vi vender hurtigt tilbage med et uforpligtende tilbud.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CMSGallery gallerySlug="transport4u" maxItems={3} title="Vores biler og udstyr" subtitle="Se et udvalg af bilerne i vores flåde" />
        </div>
      </section>
    </div>
  )
}
